import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo
} from 'react';
import { Dialog, DialogTitle, DialogContent, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import TextInput from '../Inputs/TextInput';
import BaseButton from '../Buttons/BaseButton';
import ShowPassword from '../Adornments/ShowPassword';
import PasswordInputStrengthMeter from '../PasswordStrengthIndicator/PasswordInputStrengthMeter';
import { ResetPasswordAPI } from '../../API/UserAPI';
import Recaptcha from '../Captcha/TurnstileManagedCaptcha';
import _get from 'lodash/get';
import SweetAlert from '../../Components/Alerts/SweetAlert';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCaptcha } from '../../Hooks/useCaptcha';

const getConfirmPasswordSchema = passwordKey =>
  yup
    .string()
    .required('Ingresa de nuevo la contraseña')
    .oneOf([yup.ref(passwordKey)], 'Las contraseñas deben ser iguales');

const schema = yup.object({
  currentPassword: yup
    .string()
    .required('Ingresa tu contraseña actual')
    .min(8, 'Debes usar al menos 8 caracteres')
    .notOneOf(
      [yup.ref('newPassword')],
      'La nueva contraseña debe ser diferente a la actual'
    ),
  newPassword: yup
    .string()
    .required('Ingresa una nueva contraseña')
    .min(8, 'Debes usar al menos 8 caracteres')
    .notOneOf(
      [yup.ref('currentPassword')],
      'La nueva contraseña debe ser diferente a la actual'
    ),

  confirmPassword: getConfirmPasswordSchema('newPassword')
});

const ChangePasswordDialog = ({ open, onClose, setAlert, email }) => {
  const classes = useStyles();
  const [newPasswordShown, setNewPasswordShown] = useState(false);
  const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
  const [currentPasswordShown, setCurrentPasswordShown] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const inputRef = useRef(null);
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);
  const prevNewPwd = useRef('');
  const {
    captchaRef: recaptchaRef,
    handlers: captchaHandlers,
    captchaToken: captcha
  } = useCaptcha();

  const {
    handleSubmit,
    register,
    errors,
    watch,
    setValue,
    formState: { isValid: formIsValid }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  const newPwd = watch('newPassword');
  const confirmPwd = watch('confirmPassword');

  useEffect(() => {
    if (newPwd && newPwd !== prevNewPwd.current && confirmPwd) {
      prevNewPwd.current = newPwd;
      setValue('confirmPassword', confirmPwd, {
        shouldValidate: true
      });
    }
  }, [setValue, newPwd, confirmPwd]);

  const formValid = useMemo(() => {
    return formIsValid && Boolean(captcha);
  }, [formIsValid, captcha]);

  const handleFormSubmit = useCallback(
    async data => {
      if (passwordErrors.length > 0) {
        setAlert({
          type: 'error',
          message: passwordErrors[0]
        });
        return;
      }

      try {
        captchaHandlers.reset();

        const response = await ResetPasswordAPI(
          {
            email,
            currentPassword: data.currentPassword,
            newPassword: data.newPassword
          },
          captcha
        );

        if (response.success && _get(response, 'data.status') === 'success') {
          setAlert({
            type: 'success',
            message: 'Contraseña actualizada correctamente'
          });

          onClose();
          return;
        }

        const responseErrors = _get(response, 'error.response.data.errors', {});

        if (responseErrors.recaptcha) {
          setAlert({
            type: 'error',
            message: 'El captcha no es válido'
          });
          return;
        }

        if (responseErrors.password) {
          setAlert({
            type: 'error',
            message: responseErrors.password
          });
          return;
        }

        setAlert({
          type: 'error',
          message: 'Error al actualizar la contraseña'
        });
      } catch (error) {
        setAlert({
          type: 'error',
          message:
            'Ocurrió un error inesperado. Por favor, inténtalo de nuevo más tarde.'
        });
      }
    },
    [email, passwordErrors, setAlert, onClose, captcha]
  );

  const onToggleNewPasswordShown = useCallback(() => {
    setNewPasswordShown(v => !v);
  }, []);

  const onToggleConfirmPasswordShown = useCallback(() => {
    setConfirmPasswordShown(v => !v);
  }, []);

  const handlePasswordErrors = useCallback(validationErrors => {
    setPasswordErrors(validationErrors);
    const isValidPassword = validationErrors.length === 0;
    setIsPasswordStrong(isValidPassword);
  }, []);

  const alertStyles = {
    root: classes.customSweetAlert,
    message: classes.sweetAlertText
  };

  const showAuthenticationTypeBanner = useCallback(() => {
    return (
      <SweetAlert
        id="ChangePassword_authentication_alert"
        type="info"
        classes={alertStyles}
        message={
          'Para mayor seguridad, vincula una red social a tu cuenta para iniciar sesión o usa un administrador de contraseña'
        }
      />
    );
  }, [alertStyles]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {'Cambiar contraseña'}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {showAuthenticationTypeBanner()}
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.inputContainer}>
              <TextInput
                inputRef={register}
                id="ChangePassword_input_currentPassword"
                required
                fullWidth
                label="Contraseña actual"
                error={Boolean(errors.currentPassword)}
                helperText={
                  errors.currentPassword && errors.currentPassword.message
                }
                name="currentPassword"
                type={currentPasswordShown ? 'text' : 'password'}
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <ShowPassword
                      togglePasswordShow={() =>
                        setCurrentPasswordShown(v => !v)
                      }
                      passwordShown={currentPasswordShown}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} className={classes.inputContainer}>
              <PasswordInputStrengthMeter
                password={watch('newPassword') || ''}
                currentPassword={watch('currentPassword')}
                onErrorsChange={handlePasswordErrors}
                withInput={true}
                inputRef={register}
                required
                fullWidth
                label="Nueva contraseña"
                error={
                  isPasswordStrong
                    ? false
                    : Boolean(errors.newPassword || passwordErrors.length > 0)
                }
                helperText=""
                name="newPassword"
                type={newPasswordShown ? 'text' : 'password'}
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <ShowPassword
                      togglePasswordShow={onToggleNewPasswordShown}
                      passwordShown={newPasswordShown}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                inputRef={register}
                id="ChangePassword_input_confirmPassword"
                required
                fullWidth
                label="Confirmar contraseña"
                error={Boolean(errors.confirmPassword)}
                helperText={
                  errors.confirmPassword && errors.confirmPassword.message
                }
                name="confirmPassword"
                type={confirmPasswordShown ? 'text' : 'password'}
                InputLabelProps={{
                  classes: { root: classes.inputLabel },
                  shrink: Boolean(watch('confirmPassword'))
                }}
                InputProps={{
                  autoComplete: 'off',
                  notched: Boolean(watch('confirmPassword')),
                  endAdornment: (
                    <ShowPassword
                      togglePasswordShow={onToggleConfirmPasswordShown}
                      passwordShown={confirmPasswordShown}
                    />
                  )
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <div ref={inputRef} className={classes.recaptchaContainer}>
                <Recaptcha
                  captchaRef={recaptchaRef}
                  referenceEl={inputRef}
                  heightScale={1}
                  locale="es"
                  onResolved={captchaHandlers.onResolved}
                  onExpired={captchaHandlers.onExpired}
                  onError={captchaHandlers.onError}
                  onLoaded={captchaHandlers.onLoaded}
                />
              </div>
            </Grid>

            <Grid item xs={12} className={classes.actionContainer}>
              <BaseButton onClick={onClose} variant="outlined" color="primary">
                Cancelar
              </BaseButton>
              <BaseButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!formValid}
              >
                Confirmar
              </BaseButton>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    padding: theme.spacing(2),
    minHeight: '500px',
    maxWidth: '550px'
  },
  dialogTitle: {
    textAlign: 'center',
    padding: theme.spacing(3),
    '& h2': {
      fontSize: '1.75rem',
      fontWeight: 600
    }
  },
  inputLabel: {
    transform: 'translate(14px, 16px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
      backgroundColor: theme.palette.background.paper,
      padding: '0 4px'
    }
  },
  dialogContent: {
    padding: theme.spacing(3),
    '& .MuiInputLabel-root': {
      fontSize: '1rem'
    },
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      padding: theme.spacing(1.5)
    }
  },
  actionContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4)
  },
  inputContainer: {
    marginBottom: theme.spacing(2)
  },
  recaptchaContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '85px',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2)
    }
  },
  sweetAlert: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  sweetAlertText: {
    fontSize: '0.9rem',
    textAlign: 'center'
  },
  customSweetAlert: {
    width: '100%',
    margin: theme.spacing(0, 0, 2),
    maxWidth: '100%',
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5)
  },

  inputRoot: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[300]
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(1.75, 1.5)
    }
  },

  passwordMeter: {
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: theme.palette.background.paper,
        padding: '0 4px'
      }
    }
  }
}));

export default ChangePasswordDialog;
